import { useCallback, useMemo, useState } from "react";
import { useNavigate, useRouteLoaderData } from "@remix-run/react";

import { useLabels } from "~/hooks/useLabels";
import { useRouteAction } from "~/hooks/useRouteAction";
import { useUserContext } from "~/hooks/useUserContext";
import { sendMessageToHost } from "~/utils/events";
import { getArtworkFromData } from "~/utils/data";
import { track } from "~/utils/track";
import { pathFor } from "~/utils/url";
import { ModuleCompleteModal } from "~/components/moduleCompleteModal/ModuleCompleteModal";
import { Box } from "~/components/box/Box";
import { HtmlContent } from "~/components/htmlContent/HtmlContent";
import { ReflectionCard } from "~/components/reflectionCard/ReflectionCard";
import { RouteType, Action } from "~/constants";
import { useToasts } from "~/components/toast/useToasts";
import { Accordion } from "~/components/accordion/Accordion";
import { Button } from "~/components/button/Button";
import { Text } from "~/components/text/Text";
import { Icon } from "~/components/icon/Icon";
import { SheetModalGrabber } from "~/components/sheetModal/SheetModalGrabber";
import { useSheetModal } from "~/components/sheetModal/SheetModal";

import type { ReactNode } from "react";
import type { IconProps } from "~/components/icon/Icon";
import type { ReflectionCardSaveEvent } from "~/components/reflectionCard/ReflectionCard";
import type { TrackEventData } from "~/utils/track";
import type { SessionRouteLoaderData } from "./route";

interface SessionCompletionItem {
  color: string;
  description: string;
  icon: IconProps["icon"];
  iconVariant?: IconProps["variant"];
  title: string;
  onToggle: (open: boolean) => void;
  render: () => ReactNode;
}

export interface SessionReviewReflectProps {
  onRequestClose?: () => void;
  isDesktopLayout?: boolean;
}

export const SessionReviewReflect = ({
  onRequestClose,
  isDesktopLayout,
}: SessionReviewReflectProps) => {
  const { t } = useLabels();

  const { currentUser, promptLogin } = useUserContext();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const navigate = useNavigate();

  const { submitAction } = useRouteAction();

  const { dismissAllToasts } = useToasts();

  const { dragControls } = useSheetModal();

  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);

  const trackingData = useMemo<TrackEventData>(
    () => ({
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      trigger: "sessionNotebook",
    }),
    [data.session.class?.slug, data.session.position],
  );

  const nextSession = useMemo(
    () =>
      data.classItems.modules?.nodes?.flatMap((m) => m.sessions?.nodes)?.[
        data.session.position
      ],
    [data.classItems.modules?.nodes, data.session.position],
  );

  const isModuleLastSession = useMemo(() => {
    const modules = data.session.module?.sessions?.nodes;
    const sessionIndex =
      modules?.findIndex((x) => x.position === data.session.position) ?? 0;
    return sessionIndex + 1 === modules?.length;
  }, [data.session.module?.sessions?.nodes, data.session.position]);

  // Save the user response
  const handleSaveReflection = useCallback(
    async (data: { id: string; value: string }) => {
      await submitAction({
        intent: Action.SaveReflectionResponse,
        input: {
          reflectionId: data.id,
          content: data.value,
        },
      });
    },
    [submitAction],
  );

  const items = useMemo<SessionCompletionItem[]>(() => {
    return [
      {
        title: t("session.completed.modal.review.title"),
        description: t("session.completed.modal.review.description"),
        color: "var(--color-black)",
        icon: "list-ul",
        onToggle(open) {
          if (open) {
            track("crViewReview", trackingData);
          }
        },
        render: () => (
          <Box direction="column" gap="var(--size-2)">
            <HtmlContent html={data.session.takewaysHtml} />
          </Box>
        ),
      },
      {
        title: t("session.completed.modal.reflect.title"),
        description: t("session.completed.modal.reflect.description"),
        color: "var(--color-black)",
        icon: "pen-field",
        onToggle(open) {
          if (open) {
            track("crViewReflection", trackingData);
          }
        },
        render: () => (
          <Box direction="column" gap="var(--size-2)">
            {data.session.reflections?.map((reflection) => {
              const handleRequestEdit = () => {
                // Don't track edits
                if (reflection.user?.content) return;
                track("crReflectionStart", trackingData);
              };

              const handleSave = async (event: ReflectionCardSaveEvent) => {
                await handleSaveReflection({
                  id: reflection.id,
                  value: event.value,
                });

                track("crReflectionSave", trackingData);
              };

              return (
                <ReflectionCard
                  key={reflection.id}
                  question={reflection.questionHtml}
                  response={reflection.responseHtml}
                  userResponse={reflection.user?.content ?? undefined}
                  userResponseHtml={reflection.user?.contentHtml ?? undefined}
                  onRequestEdit={handleRequestEdit}
                  onSave={handleSave}
                />
              );
            })}
          </Box>
        ),
      },
    ];
  }, [
    data.session.reflections,
    data.session.takewaysHtml,
    handleSaveReflection,
    t,
    trackingData,
  ]);

  const exitSession = () => {
    sendMessageToHost({
      navigation: {
        dismiss: {
          all: false,
        },
      },
    });
  };

  const handleNextClick = useCallback(() => {
    // If the next content is a quiz
    if (isModuleLastSession && data.session.module?.quiz) {
      setIsModuleModalOpen(true);
      return;
    }

    // Go to the next session if it exists
    if (nextSession) {
      navigate(
        pathFor(RouteType.SESSION, {
          class: data.session.class?.slug!,
          session: String(nextSession.position),
        }),
      );

      return;
    }

    // Fallback
    exitSession();
  }, [
    data.session.class?.slug,
    data.session.module?.quiz,
    isModuleLastSession,
    navigate,
    nextSession,
  ]);

  const handleCloseClick = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  const handleModuleModalRequestClose = useCallback(() => {
    setIsModuleModalOpen(false);
  }, []);

  const handleModuleModalPrimaryClick = useCallback(() => {
    dismissAllToasts();

    if (!currentUser) {
      promptLogin();
      return;
    }

    setIsModuleModalOpen(false);

    // Goto the quiz if it exists
    if (data.session.module?.quiz) {
      navigate(
        pathFor(RouteType.QUIZ, {
          class: data.session.class?.slug!,
          quiz: String(data.session.module.quiz.position),
        }),
      );

      track("crQuizStart", {
        ...trackingData,
        trigger: "moduleCompleteModal",
      });

      return;
    }

    // Goto the next session if it exists
    if (nextSession) {
      navigate(
        pathFor(RouteType.SESSION, {
          class: data.session.class?.slug!,
          session: String(nextSession.position),
        }),
      );

      return;
    }

    // Fallback
    exitSession();
  }, [
    currentUser,
    data.session.class?.slug,
    data.session.module?.quiz,
    dismissAllToasts,
    navigate,
    nextSession,
    promptLogin,
    trackingData,
  ]);

  const handleModuleModalSecondaryClick = useCallback(() => {
    dismissAllToasts();
    setIsModuleModalOpen(false);

    track("crQuizSkip", trackingData);

    if (nextSession) {
      navigate(
        pathFor(RouteType.SESSION, {
          class: data.session.class?.slug!,
          session: String(nextSession.position),
        }),
      );
    } else {
      exitSession();
    }
  }, [
    data.session.class?.slug,
    dismissAllToasts,
    navigate,
    nextSession,
    trackingData,
  ]);

  const renderLabel = useCallback((item: SessionCompletionItem) => {
    return (
      <Box gap={12}>
        <Box
          align="start"
          justify="center"
          style={{ paddingTop: "var(--size-0-5)" }}
        >
          <Icon
            icon={item.icon}
            variant={item.iconVariant}
            color="var(--color-black)"
            size="lg"
          />
        </Box>

        <Box direction="column" align="start" gap={0}>
          <Text size="lg" weight="semibold">
            {item.title}
          </Text>
          <Text size="sm">{item.description}</Text>
        </Box>
      </Box>
    );
  }, []);

  return (
    <>
      <div className="SessionCompleted">
        <div
          className="SessionCompleted__header"
          style={{
            touchAction: "none",
          }}
          onPointerDown={(e) => {
            dragControls?.start(e);
          }}
        >
          {!isDesktopLayout && (
            <div className="SessionCompleted__header-grabber">
              <SheetModalGrabber />
            </div>
          )}

          <div className="SessionCompleted__header-icon">
            <Icon icon="check" size="lg" color="white" />
          </div>

          <Text preset="pretitle">
            {t("session.completed.modal.preTitle", {
              number: data.session.position,
            })}
          </Text>

          <Text weight="bold" lineHeight="tight" size="5xl">
            {t("session.completed.modal.title")}
          </Text>

          {isDesktopLayout && (
            <Button
              appearance="control"
              icon="xmark"
              label={t("common.cta.close")}
              shape="circle"
              size="sm"
              onClick={handleCloseClick}
            />
          )}
        </div>

        <div
          className="SessionCompleted__body"
          style={{
            "--html-content-list-margin": "0",
            "--html-content-list-item-margin": "0 0 var(--size-1) 0",
          }}
        >
          <Accordion
            style={{
              ["--accordion-button-padding"]: "var(--size-2)",
              ["--accordion-panel-padding"]: "var(--size-2)",
              borderBlockEnd: "var(--border)",
            }}
            variant="chevron"
          >
            {items?.map((item) => (
              <Accordion.Item
                key={item.title}
                label={renderLabel(item)}
                onToggle={item.onToggle}
              >
                {item.render()}
              </Accordion.Item>
            ))}
          </Accordion>
        </div>

        <div className="SessionCompleted__footer">
          <Button fill onClick={handleNextClick}>
            {t("common.cta.next")}
          </Button>
        </div>
      </div>

      {data.session.module?.quiz && (
        <ModuleCompleteModal
          isOpen={isModuleModalOpen}
          artwork={getArtworkFromData(data.session.module.artwork, "16:9")?.url}
          title={t("session.completed.modal.quiz.title")}
          description={t("session.completed.modal.quiz.description", {
            module: data.session.module.title,
          })}
          tags={[
            {
              text: t("session.completed.modal.quiz.meta", {
                count: data.session.module.quiz.questions.length,
              }),
            },
          ]}
          onPrimaryClick={handleModuleModalPrimaryClick}
          onRequestClose={handleModuleModalRequestClose}
          onSecondaryClick={handleModuleModalSecondaryClick}
        />
      )}
    </>
  );
};
